import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shuffle, Sparkles, Loader2 } from "lucide-react";
import { getConfig, remixVideo, errText } from "../lib/api";

const SURPRISE = "surprise";
let cfgCache = null;

export default function RemixDialog({ video, open, onOpenChange }) {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(cfgCache);
  const [tone, setTone] = useState(SURPRISE);
  const [style, setStyle] = useState(SURPRISE);
  const [customStyle, setCustomStyle] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTone(SURPRISE); setStyle(SURPRISE); setCustomStyle(video?.custom_style || "");
    if (!cfgCache) getConfig().then((c) => { cfgCache = c; setCfg(c); }).catch(() => {});
  }, [open, video]);

  const tones = cfg?.tones || [];
  const styles = (cfg?.visual_styles || []).filter((s) => s.id !== "none");
  const name = (id, list) => list.find((x) => x.id === id)?.name || id;

  const go = async () => {
    if (!video) return;
    setBusy(true);
    try {
      const p = await remixVideo(video.id, {
        tone: tone === SURPRISE ? null : tone,
        visual_style_id: style === SURPRISE ? null : style,
        custom_style: style === "custom" ? customStyle : null,
      });
      toast.success(`Remixing — ${name(p.tone, tones)} tone, ${name(p.visual_style_id, styles)} style`);
      onOpenChange(false);
      navigate(`/video/${p.id}`);
    } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="remix-dialog" className="bg-[#0F1118] border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Shuffle className="h-5 w-5 text-[#FF6A5E]" /> Remix this ad</DialogTitle>
          <DialogDescription className="text-slate-400">
            Same product and link. New narration and a new angle every time — choose the look, or let us surprise you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs text-slate-400">Tone</label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger data-testid="remix-tone" className="mt-1.5 bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={SURPRISE}><span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[#FF6A5E]" /> Surprise me (different from current)</span></SelectItem>
                {tones.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.id === video?.tone ? " · current" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-400">Visual style</label>
            <Select value={style} onValueChange={setStyle}>
              <SelectTrigger data-testid="remix-style" className="mt-1.5 bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={SURPRISE}><span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[#FF6A5E]" /> Surprise me (different from current)</span></SelectItem>
                {styles.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.id === video?.visual_style_id ? " · current" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            {style === "custom" && (
              <Input data-testid="remix-custom-style" value={customStyle} onChange={(e) => setCustomStyle(e.target.value)} placeholder="Describe the look, e.g. neon cyberpunk watercolor" className="mt-2 bg-[#0B0C10] border-white/10 h-11" />
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full text-slate-300 hover:text-white hover:bg-white/5">Cancel</Button>
          <Button data-testid="remix-submit" onClick={go} disabled={busy || (style === "custom" && !customStyle.trim())} className="rounded-full px-6 bg-[#FF453A] hover:bg-[#ff5c52] text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shuffle className="h-4 w-4 mr-2" />} Remix now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
